import * as React from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { PlayfulNavContext } from '@/lib/playful-nav-context';

const appName = process.env.REACT_APP_NAME;
const hasCustomLogos = appName === 'canaleducacao' || appName === 'maiseducacao';

const dashboardContentVariants = cva('ml-0 w-full max-w-screen overflow-x-clip bg-neutral-bright ', {
  variants: {
    variant: {
      default: 'flex flex-col justify-start flex-wrap',
      plain: '',
      column: 'flex flex-col items-stretch justify-start'
    },
    minHeight: {
      default: 'min-h-screen', // 100vh
      minusHeader: 'min-h-[calc(100vh-68px)] md:min-h-screen' // 100vh - 68px
    }
  },
  defaultVariants: {
    variant: 'default',
    minHeight: 'default'
  }
});

// Offset lateral do conteúdo: no modo lúdico não há sidebar, então ml-0; no
// corporativo o conteúdo abre espaço p/ a sidebar fixa (64px, ou 260px no create).
const viewModeClass = (viewMode, playful) => {
  const map = {
    default: playful ? 'md:ml-0 md:w-full' : 'md:ml-16 md:w-[calc(100%-64px)]',
    create: playful ? 'md:ml-0 md:w-full' : 'md:ml-[260px] md:w-[calc(100%-260px)]'
  };
  return map[viewMode] || map.default;
};

// Padding: o modo lúdico reserva espaço p/ a bottom bar (pb-[88px]); o corporativo não.
// pt mobile lúdico 124px: o header playful (logo h-9) mede ~111px — com 110px o
// breadcrumb ficava atrás dele em toda tela mobile.
const paddingClass = (padding, playful) => {
  const map = {
    default: playful ? '!px-4 pb-[110px] pt-[124px] md:!px-11 md:pb-[110px] md:pt-[104px]' : '!px-4 pb-4 pt-[110px] md:!px-11 md:pb-11 md:pt-[104px]',
    none: '!p-0'
  };
  return map[padding] || map.default;
};

const DashboardContent = React.forwardRef(({ className, variant, viewMode, padding, breadcrumbs, breadcrumbsVerified, breadcrumbsActions, children, ...props }, ref) => {
  const minHeightVariant = hasCustomLogos ? 'minusHeader' : 'default';
  const playfulNav = React.useContext(PlayfulNavContext);

  return (
    <main
      ref={ref}
      className={cn('c-dashboard-content', dashboardContentVariants({ variant, minHeight: minHeightVariant }), viewModeClass(viewMode, playfulNav), paddingClass(padding, playfulNav), className)}
      {...props}
    >
      {breadcrumbs && (
        <Breadcrumbs
          data={breadcrumbs}
          innerContent={breadcrumbsVerified}
          actions={breadcrumbsActions}
        />
      )}

      {children}
    </main>
  );
});
DashboardContent.displayName = 'DashboardContent';

const Dashboard = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-dashboard', 'relative flex min-h-screen w-full flex-wrap items-start justify-between', className)}
    {...props}
  />
));
Dashboard.displayName = 'Dashboard';

export { DashboardContent, Dashboard };
